import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast/toast.service';
import { IBuildProgrammeActivityLogicService, IBuildProgrammeActivityMappedItem } from '@app/logic/build-programme-activity';
import { IBusinessAccountEditBaseDto } from '@app/logic/business-accounts/interfaces/i.business-account.dto';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import { LotBuildProgrammeEventService } from '@app/views/lot/build/services/lot-build-programme-event.service';
import {
    BUSINESS_ACCOUNT_SEARCH_TYPE_ENUM,
    BUSINESS_ACCOUNT_STATUS_ENUM,
    BusinessAccountSearchTypeEnumId,
    BusinessAccountStatusEnumId, IBuildProgrammeActivityAffectedBySupplierChangeDto, IBuildProgrammeActivityDto, IBuildProgrammeActivitySupplierChangeDetailsDto,
    IBuildProgrammeActivityUpdateSupplierDto, ILookupDto,
    ISkinnyBusinessAccountDto
} from '@classictechsolutions/hubapi-transpiled-enums';
import { FeatureToggleStatesService } from '@app/core/services/feature-toggle-states/feature-toggle-states.service';
import { BlockingDialogComponent } from '@app/shared/components/dialog/blocking-dialog/blocking-dialog.component';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { take } from 'rxjs';

/** A row in the affected activities tables - the dto plus the state of its checkbox */
interface IAffectedActivityRow extends IBuildProgrammeActivityAffectedBySupplierChangeDto {
    isSelected: boolean;
}

interface IData {
    buildProgrammeActivity: IBuildProgrammeActivityMappedItem;
    lotIsPrimaryUnit: boolean;
    selectedBusinessAccount: ISkinnyBusinessAccountDto;
    lotId: number;
}

@Component({
    templateUrl: './set-build-programme-activity-supplier-dialog.component.html',
    styleUrls: ['./set-build-programme-activity-supplier-dialog.component.scss'],
    standalone: false
})
export class SetBuildProgrammeActivitySupplierDialogComponent
    extends BaseDialogFormViewDirective<IBuildProgrammeActivityDto, IBuildProgrammeActivityMappedItem, IBuildProgrammeActivityLogicService>
    implements OnInit {
    public static readonly MIN_WIDTH = '60%';
    public blockingRef: MatDialogRef<BlockingDialogComponent>;

    public get mappedItem(): IBuildProgrammeActivityMappedItem {
        return this.data.buildProgrammeActivity;
    }

    public selectedBusinessAccount: IBusinessAccountEditBaseDto;
    public businessAccountFindParams: { [param: string]: any } = {};
    public tradeTypeIds: number[] = [];
    public selectedTradeType: any;
    public selectedSupplyType: any;
    public tradeType: any;
    public tradeTypesInSupplierAndActivity: Array<ILookupDto> = [];
    public supplyTypesInSupplierAndActivity: Array<ILookupDto> = [];
    public noTradeTypeOptions: boolean;
    public noSupplyTypeOptions: boolean;
    public assignSameSupplierToSecondaryLot = false;

    public updatableActivities: IAffectedActivityRow[] = [];
    public nonUpdatableActivities: IAffectedActivityRow[] = [];
    /** False until a supplier has been picked and the api has answered */
    public affectedActivitiesLoaded = false;
    // Business Account Search Queries
    public statusQuery: BusinessAccountStatusEnumId[];
    public searchTypeQuery: BusinessAccountSearchTypeEnumId;

    public get displayTradeTypesSelect(): boolean {
        return this.tradeTypesInSupplierAndActivity.length > 1;
    }

    public get displaySupplyTypesSelect(): boolean {
        return this.supplyTypesInSupplierAndActivity.length > 1;
    }

    constructor(
        public readonly dialogRef: MatDialogRef<SetBuildProgrammeActivitySupplierDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: IData,
        @Inject(ToastService) public readonly toastService: ToastService,
        @Inject(LotBuildProgrammeEventService) public readonly lotBuildProgrammeEvents: LotBuildProgrammeEventService,
        public readonly cbDialog: CbDialogService,
        public readonly featureToggleService: FeatureToggleStatesService,
    ) {
        super(dialogRef, toastService);
        this.selectedBusinessAccount = this?.data?.selectedBusinessAccount as any;
    }

    public ngOnInit(): void {
        this.setSearchParams();
        if (this.selectedBusinessAccount) {
            this.businessAccountSet();
        }
    }

    public setSearchParams(): void {
        const statusIds = BUSINESS_ACCOUNT_STATUS_ENUM.toLookup().map((status) => status.id);
        const validStatusIds = statusIds.filter((status: number) => {
            return status === BUSINESS_ACCOUNT_STATUS_ENUM.Active ||
                status === BUSINESS_ACCOUNT_STATUS_ENUM.Overridden;
        });

        this.statusQuery = validStatusIds;
        this.searchTypeQuery = BUSINESS_ACCOUNT_SEARCH_TYPE_ENUM.NotRatesOnly;

        this.tradeTypeIds = this.mappedItem.tradeTypes.map(tt => tt.id);

        // One source for both controls - AC#0 requires the dropdown and the search icon to show
        // the same list, so they must be given the same parameters
        this.businessAccountFindParams = {
            s: validStatusIds,
            t: BUSINESS_ACCOUNT_SEARCH_TYPE_ENUM.NotRatesOnly,
            tt: this.tradeTypeIds
        };
    }

    public businessAccountSet(): void {
        this.setUpTradeTypes();
        if(this.featureToggleService.isSupplierTypesEnabled) {
            this.setUpSupplyTypes();
        }
        this.loadAffectedActivities();
    }

    private loadAffectedActivities(): void {
        if (!this.selectedBusinessAccount) {
            this.clearAffectedActivities();
            return;
        }

        // Trade and Supply Type are deliberately omitted - without them the api returns
        // every other activity that currently has the same supplier, regardless of trade type
        const dto = {
            buildProgrammeActivityId: this.mappedItem.id,
            supplierId: this.selectedBusinessAccount.id,
            lotId: this.data.lotIsPrimaryUnit ? this.data.lotId : this.mappedItem.lotId
        } as IBuildProgrammeActivitySupplierChangeDetailsDto;

        this.mappedItem
            .getAffectedActivitiesForSupplierChange(dto)
            .pipe(take(1))
            .subscribe(results => this.setAffectedActivities(results));
    }

    public setUpTradeTypes(): void {
        this.tradeTypesInSupplierAndActivity = [];
        this.selectedTradeType = undefined;

        if (this.mappedItem.tradeTypes
            && this.mappedItem.tradeTypes.length === 1) {
            this.tradeTypesInSupplierAndActivity.push(this.mappedItem.tradeTypes[0]);
            this.selectedTradeType = this.mappedItem.tradeTypes[0];
        } else if (this.mappedItem.tradeTypes && this.mappedItem.tradeTypes.length > 1) {
            this.mappedItem.tradeTypes.forEach(activityTt => {
                if (this.selectedBusinessAccount?.tradeTypes?.some(accountTT => accountTT === activityTt.id)) {
                    this.tradeTypesInSupplierAndActivity.push(activityTt);
                }
            });
        }

        if (this.tradeTypesInSupplierAndActivity.length === 1) {
            this.selectedTradeType = this.tradeTypesInSupplierAndActivity[0];
        }

        if (this.tradeTypesInSupplierAndActivity.length < 1) {
            this.noTradeTypeOptions = true;
        } else {
            this.noTradeTypeOptions = false;
        }
    }

    public setUpSupplyTypes(): void {
        this.supplyTypesInSupplierAndActivity = [];
        this.selectedSupplyType = undefined;

        if (this.mappedItem.supplyTypes
            && this.mappedItem.supplyTypes.length === 1) {
            this.supplyTypesInSupplierAndActivity.push(this.mappedItem.supplyTypes[0]);
            this.selectedSupplyType = this.mappedItem.supplyTypes[0];
        } else if (this.mappedItem.supplyTypes && this.mappedItem.supplyTypes.length > 1) {
            this.mappedItem.supplyTypes.forEach(activitySt => {
                if (this.selectedBusinessAccount?.supplyTypes?.some(accountST => accountST === activitySt.id)) {
                    this.supplyTypesInSupplierAndActivity.push(activitySt);
                }
            });
        }

        if (this.supplyTypesInSupplierAndActivity.length === 1) {
            this.selectedSupplyType = this.supplyTypesInSupplierAndActivity[0];
        }

        if (this.supplyTypesInSupplierAndActivity.length < 1) {
            this.noSupplyTypeOptions = true;
        } else {
            this.noSupplyTypeOptions = false;
        }
    }

    public getBlockActivityTooltip(activity: IAffectedActivityRow): string {
        // unitCount includes this lot's own row
        const units = activity.unitCount;

        if (units > 1) {
            return `This is a block-level Activity. Any change will apply to the parent lot and to all ${units} units, including this one.`;
        }

        return 'This is a block-level Activity. Any change will apply to the parent lot.';
    }

    public setSupplier(): void {
        this.saveInProgress = true;
        this.blockingRef = this.cbDialog.block('Setting supplier, please wait...');

        const dto = {
            buildProgrammeActivityId: this.mappedItem.id,
            reassignIncompleteActivities: true,
            supplierId: this.selectedBusinessAccount.id,
            assignSameSupplierToSecondaryLot: this.assignSameSupplierToSecondaryLot,
            primaryLotId: this.data.lotIsPrimaryUnit ? this.data.lotId : undefined,
            selectedBuildProgrammeActivityIds: this.getSelectedAffectedActivityIds()
        } as IBuildProgrammeActivityUpdateSupplierDto;

        this.mappedItem.businessAccount = this.selectedBusinessAccount as any;

        this.mappedItem
            .setSupplier(dto)
            .pipe(take(1)).subscribe((results) => {
                this.blockingRef.close();
                this.dialogRef.close(results);
                this.saveInProgress = false;
            });
    }

    public setSupplierDisabled(supplierForm: NgForm): boolean {
        if (this.noTradeTypeOptions && this.noSupplyTypeOptions && supplierForm && supplierForm.dirty) {
            return false;
        }
        return !supplierForm
            || (!this.selectedTradeType && this.mappedItem.tradeTypes.length > 0)
            || (!this.selectedSupplyType && this.mappedItem.supplyTypes.length > 0);
    }

    public get allAffectedActivitiesSelected(): boolean {
        return this.updatableActivities.length > 0 &&
            this.updatableActivities.every(row => row.isSelected);
    }

    public get someAffectedActivitiesSelected(): boolean {
        const anySelected = this.updatableActivities.some(activity => activity.isSelected);
        return anySelected && !this.allAffectedActivitiesSelected;
    }

    public get canSelectAllAffectedActivities(): boolean {
        return this.updatableActivities.length > 0;
    }

    public toggleAllAffectedActivities(isChecked: boolean): void {
        this.updatableActivities.forEach(activity => {
            activity.isSelected = isChecked;
        });
    }

    /** Only this row's reason - the general rule is shown once, above the table */
    public getCannotBeUpdatedReason(activity: IAffectedActivityRow): string {
        const reasons: string[] = [];

        if (activity.isManualOrder) {
            reasons.push('is a Manual Order');
        }
        if (activity.isQuoted) {
            reasons.push('has allocated Quote Lines');
        }
        if (activity.hasBlockingStatus) {
            reasons.push(`is ${activity.statusLabel}`);
        }

        return `This activity ${reasons.join(' and ')}`;
    }

    private setAffectedActivities(
        activities: IBuildProgrammeActivityAffectedBySupplierChangeDto[]
    ): void {
        const rows = activities.map(activity => ({
            ...activity,
            isSelected: activity.canBeUpdated
        }));

        this.updatableActivities = rows.filter(activity => activity.canBeUpdated);
        this.nonUpdatableActivities = rows.filter(activity => !activity.canBeUpdated);
        this.affectedActivitiesLoaded = true;
    }

    private clearAffectedActivities(): void {
        this.updatableActivities = [];
        this.nonUpdatableActivities = [];
        this.affectedActivitiesLoaded = false;
    }

    private getSelectedAffectedActivityIds(): number[] {
        return this.updatableActivities
            .filter(activity => activity.isSelected)
            .map(activity => activity.id);
    }

    public foundAffectedActivities(): boolean {
        return this.updatableActivities.length > 0 || this.nonUpdatableActivities.length > 0;
    }
}
